import React, { useRef, useState, useEffect } from 'react';
import { Printer, X, Play, Pause, CheckCircle } from 'lucide-react';
import Barcode from '../Common/Barcode';
import { API_URL, getServerUrl } from '../../services/api';

const FicheFabricationV2 = ({ ordre, onClose, imagePrincipale }) => {
  const ficheRef = useRef(null);
  const [ficheData, setFicheData] = useState(null);
  const [fonte, setFonte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editNotes, setEditNotes] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    if (ordre?.id || ordre?.ordre?.id) {
      loadFicheData();
    }
  }, [ordre]);

  const loadFicheData = async () => {
    try {
      // L'ordre peut avoir différentes structures selon d'où il vient
      const ordreId = ordre?.ordre?.id || ordre?.id;
      console.log('FicheFabricationV2 - ordre:', ordre);
      console.log('FicheFabricationV2 - ordreId:', ordreId);
      
      if (!ordreId) {
        console.error('Aucun ID d\'ordre trouvé');
        setLoading(false);
        return;
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/atelier/fiche-fabrication/${ordreId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFicheData(data);
      } else {
        console.error('Erreur chargement fiche');
      }

      // Fonte réelle de l'OF (table of_fonte) — rien si aucune fonte saisie
      try {
        const resFonte = await fetch(`${API_URL}/atelier/fiche-fabrication/${ordreId}/fonte`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resFonte.ok) {
          const f = await resFonte.json();
          setFonte(f && f.numero_fonte ? f : null);
        }
      } catch (e) {
        setFonte(null);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const openEditNotes = () => {
    setNotesText(ficheData?.ordre?.notes || '');
    setEditNotes(true);
  };

  // ── Actions étapes ──────────────────────────────────────────────
  // ── Live timer ─────────────────────────────────────────────────
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const formatElapsed = (etape) => {
    if (!etape.date_debut) return null;
    if (etape.statut !== 'en_cours' && etape.statut !== 'pause') return null;
    const debut = new Date(etape.date_debut).getTime();
    const pauseMs = (etape.temps_pause_minutes || 0) * 60000;
    let elapsedMs;
    if (etape.statut === 'pause' && etape.heure_pause) {
      // Frozen at pause start: elapsed = (heure_pause - debut) - already accumulated pause
      elapsedMs = new Date(etape.heure_pause).getTime() - debut - pauseMs;
    } else if (etape.statut === 'pause') {
      // No heure_pause info: approximate with accumulated pause time only
      elapsedMs = now - debut - pauseMs;
    } else {
      // en_cours: live ticking
      elapsedMs = now - debut - pauseMs;
    }
    if (elapsedMs < 0) elapsedMs = 0;
    const totalSec = Math.floor(elapsedMs / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h${String(m).padStart(2,'0')}`;
    return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };
  // ────────────────────────────────────────────────────────────────

  const [actionLoading, setActionLoading] = useState(null); // etapeId en cours
  const [showTerminerFiche, setShowTerminerFiche] = useState(false);
  const [etapeTerminer, setEtapeTerminer] = useState(null);
  const [tempsTerminer, setTempsTerminer] = useState('');
  const [poidsOrUtilise, setPoidsOrUtilise] = useState('');
  const [poidsBijouEtape, setPoidsBijouEtape] = useState('');
  const [showReceptionFiche, setShowReceptionFiche] = useState(false);
  const [etapeReception, setEtapeReception] = useState(null);
  const [formReception, setFormReception] = useState({ prix_reel_externe: '', poids_or_recu_externe: '' });

  const callEtapeAction = async (etapeId, action, body = {}) => {
    setActionLoading(etapeId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/atelier/etapes/${etapeId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) { await loadFicheData(); }
      else { const d = await res.json(); alert(d.error || `Erreur ${action}`); }
    } catch { alert('Erreur réseau'); }
    finally { setActionLoading(null); }
  };

  const ouvrirTerminer = (etape) => {
    setEtapeTerminer(etape);
    // Pré-remplir avec le VRAI temps écoulé du compteur (début → maintenant − pauses),
    // modifiable avant validation ; fallback sur l'estimée si le compteur n'a pas tourné.
    let prefillMin = '';
    if (etape.date_debut && (etape.statut === 'en_cours' || etape.statut === 'pause')) {
      const debut = new Date(etape.date_debut).getTime();
      const pauseMs = (etape.temps_pause_minutes || 0) * 60000;
      const finMs = (etape.statut === 'pause' && etape.heure_pause)
        ? new Date(etape.heure_pause).getTime()
        : Date.now();
      const elapsedMin = Math.round(Math.max(0, finMs - debut - pauseMs) / 60000);
      if (elapsedMin > 0) prefillMin = String(elapsedMin);
    }
    if (prefillMin === '' && etape.duree_estimee_heures) {
      prefillMin = String(Math.round(etape.duree_estimee_heures * 60));
    }
    setTempsTerminer(prefillMin);
    setPoidsOrUtilise('');
    setPoidsBijouEtape('');
    setShowTerminerFiche(true);
  };

  const validerTerminer = async () => {
    if (!etapeTerminer) return;
    const mins = parseFloat(tempsTerminer);
    if (!mins || mins <= 0) { alert('Saisissez le temps en minutes'); return; }
    const body = { duree_reelle_heures: mins / 60 };
    if (poidsOrUtilise !== '') body.poids_or_utilise = parseFloat(poidsOrUtilise) || 0;
    if (poidsBijouEtape !== '') body.poids_bijou_etape = parseFloat(poidsBijouEtape) || 0;
    await callEtapeAction(etapeTerminer.id, 'terminer', body);
    setShowTerminerFiche(false);
    setEtapeTerminer(null);
  };

  const ouvrirReception = (etape) => {
    setEtapeReception(etape);
    setFormReception({ prix_reel_externe: etape.prix_unitaire || '', poids_or_recu_externe: '' });
    setShowReceptionFiche(true);
  };

  const validerReception = async () => {
    if (!etapeReception) return;
    await callEtapeAction(etapeReception.id, 'receptionner-externe', {
      prix_reel_externe: formReception.prix_reel_externe ? parseFloat(formReception.prix_reel_externe) : null,
      poids_or_recu_externe: formReception.poids_or_recu_externe ? parseFloat(formReception.poids_or_recu_externe) : null,
    });
    setShowReceptionFiche(false);
    setEtapeReception(null);
  };
  // ────────────────────────────────────────────────────────────────

  const saveNotes = async () => {
    const ordreId = ordre?.ordre?.id || ordre?.id;
    setSavingNotes(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/atelier/fabrication/${ordreId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ statut: ficheData?.ordre?.statut, notes: notesText || null })
      });
      if (response.ok) {
        setEditNotes(false);
        await loadFicheData();
      } else {
        alert('Erreur lors de l\'enregistrement');
      }
    } catch (e) {
      alert('Erreur réseau');
    } finally {
      setSavingNotes(false);
    }
  };

  if (!ordre) return null;
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p>Chargement de la fiche...</p>
        </div>
      </div>
    );
  }
  if (!ficheData) return null;

  // Date de fin de fabrication
  const dateFinFab = ficheData.ordre?.date_livraison_prevue 
    ? new Date(ficheData.ordre.date_livraison_prevue).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }).toUpperCase()
    : 'N/A';

  // Calculer poids — unitaire et total
  const quantitePieces = parseInt(ficheData.ordre?.quantite_pieces || 1);
  const poidsUnitaire = parseFloat(ficheData.ordre?.poids_unitaire || ficheData.ordre?.poids_metal || ficheData.poids_or || 0);
  const poidsTotal = parseFloat(ficheData.ordre?.poids_total_or || 0) || (poidsUnitaire * quantitePieces);
  const mouleNumero = ficheData.ordre?.moule_numero || '';
  const refArticle = ficheData.ordre?.ref_article || ficheData.ordre?.code_article || '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-auto p-4">
      {/* Boutons d'action */}
      <div className="no-print absolute top-4 right-4 z-50 flex space-x-2">
        <button
          onClick={openEditNotes}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2 shadow-lg"
          title="Ajouter / modifier la note importante"
        >
          <span>⚠️</span>
          <span>{ficheData?.ordre?.notes ? 'Modifier note' : '+ Note'}</span>
        </button>
        <button
          onClick={handlePrint}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 shadow-lg"
        >
          <Printer size={18} />
          <span>Imprimer</span>
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center space-x-2 shadow-lg"
        >
          <X size={18} />
          <span>Fermer</span>
        </button>
      </div>

      {/* Modal édition note */}
      {editNotes && (
        <div className="no-print fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-red-700 mb-1">⚠️ Note importante</h3>
            <p className="text-sm text-gray-500 mb-3">Visible en rouge sur la fiche de fabrication</p>
            <textarea
              value={notesText}
              onChange={e => setNotesText(e.target.value)}
              rows={4}
              autoFocus
              className="w-full border-2 border-red-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-400 bg-red-50 text-red-900 font-medium resize-none"
              placeholder="Ex: Taille 54, gravure intérieure, attention rhodiage renforcé..."
            />
            <div className="flex gap-3 mt-4 justify-end">
              <button
                onClick={() => setEditNotes(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={saveNotes}
                disabled={savingNotes}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-semibold"
              >
                {savingNotes ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fiche A4 PAYSAGE - 2 COLONNES (pliée en 2 pour les ouvriers) */}
      <div ref={ficheRef} className="print-content" style={{
        width: '297mm',
        height: '210mm',
        margin: '0 auto',
        backgroundColor: 'white',
        padding: '6mm',
        fontFamily: 'Arial, sans-serif',
        overflow: 'hidden',
        boxSizing: 'border-box',
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0'
      }}>
        {/* Ligne de pliage au centre */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: '50%', transform: 'translateX(-50%)',
          width: '1px',
          borderLeft: '1px dashed #aaa',
          pointerEvents: 'none', zIndex: 1
        }} />
        
        {/* ==================== COLONNE GAUCHE (Face gauche après pliage) ==================== */}
        <div style={{ paddingRight: '4mm', overflow: 'hidden' }}>
          {/* Date en haut */}
          <div style={{ fontSize: '8pt', marginBottom: '2mm' }}>
            {new Date().toLocaleDateString('fr-FR')}
          </div>

          {/* Titre centré */}
          <div style={{ textAlign: 'center', fontSize: '10pt', fontWeight: 'bold', marginBottom: '1mm' }}>
            Fabrication N° {ficheData.ordre?.numero_ordre || '0000000000'}
          </div>
          {/* Quantité et moule */}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '8pt', marginBottom: '2mm', fontWeight: 'bold' }}>
            <span>Ref: {refArticle}</span>
            <span>Qte: {quantitePieces} piece(s)</span>
            {mouleNumero && <span>Moule: {mouleNumero}</span>}
          </div>

          {/* Grand encadré : Étapes + Code-barre */}
          <div style={{ border: '1px solid black', padding: '3mm', marginBottom: '3mm', minHeight: '80mm' }}>
            {/* Note importante en rouge */}
            {(ficheData.ordre?.notes || ficheData.ordre?.description_details) && (
              <div style={{ background: '#fff0f0', border: '1.5px solid red', borderRadius: '2mm', padding: '1.5mm 2mm', marginBottom: '2mm', fontSize: '8pt', color: 'red', fontWeight: 'bold' }}>
                ⚠️ NOTE : {ficheData.ordre.notes || ficheData.ordre.description_details}
              </div>
            )}
            <div style={{ fontSize: '9pt', fontWeight: 'bold', lineHeight: '1.5' }}>
              {/* Toutes les étapes : devis (noir/vert) + hors-devis ajoutées ouvrier (orange) */}
              {ficheData.etapes_devis && ficheData.etapes_devis.length > 0 ? (
                ficheData.etapes_devis.map((etape, idx) => {
                  const estTerminee = etape.statut === 'terminee';
                  const estEnCours = etape.statut === 'en_cours';
                  const horsDevis = !etape.in_devis;
                  const couleur = estTerminee ? '#1a7a1a' : horsDevis ? '#c47000' : estEnCours ? '#e06000' : '#111';
                  const symbole = estTerminee ? '✓' : estEnCours ? '⏳' : horsDevis ? '+' : '○';
                  const estInterne = etape.type_ressource === 'interne';
                  return (
                    <div key={idx} style={{
                      marginBottom: '1.5mm',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2mm',
                      borderLeft: `3px solid ${couleur}`,
                      paddingLeft: '2mm',
                      background: horsDevis ? '#fff8e1' : 'transparent',
                    }}>
                      <span style={{ minWidth: '5mm', fontSize: '7pt', color: '#666', fontWeight: 'normal' }}>
                        {String(idx + 1).padStart(2, '0')}.
                      </span>
                      <span style={{ flex: 1, color: couleur, fontWeight: estTerminee ? 'bold' : 'normal' }}>
                        {symbole} {etape.nom_etape || etape.nom}
                        {horsDevis && <span style={{ fontSize: '6pt', marginLeft: '1mm', color: '#c47000' }}>(hors devis)</span>}
                      </span>
                      <span style={{ fontSize: '6pt', color: estInterne ? '#2563eb' : '#ea580c', flexShrink: 0 }}>
                        {estInterne ? '🏭' : '🔗'}
                      </span>
                      <span style={{ fontSize: '7pt', color: couleur, fontWeight: estTerminee ? 'bold' : 'normal', flexShrink: 0 }}>
                        {estTerminee ? 'Terminé' : estEnCours ? 'En cours' : 'Attente'}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div style={{ color: 'gray', fontSize: '8pt' }}>Aucune étape définie</div>
              )}
            </div>
            
            {/* Code-barre des étapes */}
            {ficheData.ordre?.code_barre && (
              <div style={{ marginTop: '5mm', textAlign: 'center' }}>
                <Barcode 
                  value={ficheData.ordre.code_barre}
                  height={50}
                  width={1.5}
                  displayValue={true}
                  fontSize={9}
                />
              </div>
            )}
          </div>

          {/* Tableau Poids unitaire produit fini */}
          <div style={{ marginBottom: '3mm' }}>
            <table style={{ width: '100%', fontSize: '7pt', borderCollapse: 'collapse', border: '1px solid black' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }} colSpan="3">
                    Poids unitaire produit fini
                  </th>
                  <th style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }} rowSpan="2">
                    PIERRES<br/>seules (en g)
                  </th>
                </tr>
                <tr>
                  <th style={{ border: '1px solid black', padding: '1mm' }}></th>
                  <th style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>NU</th>
                  <th style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>PIER. INC</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid black', padding: '1mm', fontWeight: 'bold' }}>Mini</td>
                  <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                    {(poidsTotal * 0.98).toFixed(2)}
                  </td>
                  <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                    {poidsTotal.toFixed(2)}
                  </td>
                  <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                    {(ficheData.pierres?.reduce((sum, p) => sum + parseFloat(p.poids_carats || 0), 0) || 0).toFixed(3)}
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black', padding: '1mm', fontWeight: 'bold' }}>Maxi</td>
                  <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                    {(poidsTotal * 1.02).toFixed(2)}
                  </td>
                  <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                    {(poidsTotal * 1.02).toFixed(2)}
                  </td>
                  <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                    {(ficheData.pierres?.reduce((sum, p) => sum + parseFloat(p.poids_carats || 0), 0) || 0).toFixed(3)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Total production */}
          {quantitePieces > 1 && (
            <div style={{ border: '2px solid black', padding: '2mm', marginBottom: '2mm', fontSize: '8pt', background: '#f5f5f5' }}>
              <div style={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '1mm' }}>TOTAL PRODUCTION ({quantitePieces} pieces)</div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Or total : <strong>{poidsTotal.toFixed(2)}g</strong> ({quantitePieces} x {poidsUnitaire.toFixed(2)}g)</span>
                <span>Pierres total : <strong>{((ficheData.pierres?.reduce((s, p) => s + parseFloat(p.poids_carats || 0), 0) || 0) * quantitePieces).toFixed(3)}ct</strong></span>
              </div>
            </div>
          )}

          {/* Infos commande */}
          <div style={{ border: '1px solid black', padding: '2mm', marginBottom: '3mm', fontSize: '8pt' }}>
            <div><strong>{ficheData.ordre?.numero_ordre}</strong> — {refArticle}</div>
            <div>{ficheData.ordre?.type_bijou} — {ficheData.ordre?.client_nom || 'Client'}</div>
            <div>{new Date(ficheData.ordre?.date_commande).toLocaleDateString('fr-FR')}</div>
            {ficheData.ordre?.notes && (
              <div style={{ fontSize: '7pt', color: 'red', marginTop: '1mm' }}>
                SPÉCIALE : {ficheData.ordre.notes}
              </div>
            )}
          </div>

          {/* Tableau Pierres */}
          <div>
            <table style={{ width: '100%', fontSize: '7pt', borderCollapse: 'collapse', border: '1px solid black' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid black', padding: '1mm' }}>Pierre</th>
                  <th style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>Qté</th>
                  <th style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>Poids</th>
                  <th style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>Diamètres</th>
                </tr>
              </thead>
              <tbody>
                {ficheData.pierres && ficheData.pierres.length > 0 ? (
                  ficheData.pierres.map((pierre, idx) => (
                    <tr key={idx}>
                      <td style={{ border: '1px solid black', padding: '1mm' }}>{pierre.type_pierre || pierre.reference}</td>
                      <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                        {parseFloat(pierre.quantite || 0).toFixed(0)}
                      </td>
                      <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                        {parseFloat(pierre.poids_carats || 0).toFixed(3)}
                      </td>
                      <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>-</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ border: '1px solid black', padding: '1mm', textAlign: 'center', color: 'gray' }}>
                      Aucune pierre
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ==================== COLONNE DROITE (Face droite après pliage) ==================== */}
        <div style={{ paddingLeft: '4mm', overflow: 'hidden' }}>

          {/* En-tête avec quantité */}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10pt', fontWeight: 'bold', marginBottom: '2mm' }}>
            <span>{ficheData.ordre?.numero_ordre}</span>
            <span style={{ background: '#000', color: '#fff', padding: '1mm 3mm', fontSize: '12pt' }}>{quantitePieces}</span>
          </div>

          <div style={{ border: '1px solid black', padding: '3mm', marginBottom: '2mm' }}>
            {/* Ligne 1 : Infos principales */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9pt', marginBottom: '2mm' }}>
              <div><strong>{ficheData.ordre?.client_nom || 'Client'}</strong></div>
              <div><strong>{ficheData.ordre?.metal || ficheData.ordre?.couleur_or || 'OR'}</strong> — OR 18K</div>
              <div><strong>{poidsUnitaire.toFixed(2)}g</strong> /piece</div>
            </div>
            {/* Total or */}
            <div style={{ background: '#eee', padding: '1.5mm', textAlign: 'center', fontSize: '9pt', fontWeight: 'bold', marginBottom: '2mm' }}>
              TOTAL OR : {poidsTotal.toFixed(2)}g ({quantitePieces} x {poidsUnitaire.toFixed(2)}g)
            </div>

            {/* Ligne 2 : Type bijou */}
            <div style={{ fontSize: '8pt', fontWeight: 'bold', marginBottom: '1mm' }}>
              {ficheData.ordre?.type_bijou?.toUpperCase() || 'BIJOU'} {ficheData.ordre?.description || ''}
            </div>

            {/* Ligne 3 : Modèle et Emplacement */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '7pt', marginBottom: '1mm' }}>
              <span>Modele : <strong>{ficheData.ordre?.ref_article || ficheData.ordre?.code_article || ''}</strong></span>
              <span>Emplacement : AJ / FIXE</span>
            </div>
          </div>

          {/* Commande */}
          <div style={{ border: '1px solid black', padding: '2mm', marginBottom: '2mm', fontSize: '7pt' }}>
            <strong>{ficheData.ordre?.client_nom || 'Client'}</strong> - {ficheData.ordre?.numero_ordre}
            {ficheData.ordre?.notes && (
              <div style={{ marginTop: '1mm', color: 'red', fontWeight: 'bold' }}>
                ⚠️ {ficheData.ordre.notes}
              </div>
            )}
          </div>

          {/* Fin de Fab */}
          <div style={{ border: '1px solid black', display: 'inline-block', padding: '2mm', marginBottom: '2mm', fontSize: '8pt' }}>
            Fin de Fab. : <strong>{dateFinFab}</strong>
          </div>

          {/* Code-barre principal */}
          {ficheData.ordre?.code_barre && (
            <div style={{ textAlign: 'center', marginBottom: '2mm' }}>
              <Barcode 
                value={ficheData.ordre.code_barre}
                height={40}
                width={1.5}
                displayValue={true}
                fontSize={9}
              />
            </div>
          )}

          {/* Référence interne */}
          <div style={{ fontSize: '10pt', fontWeight: 'bold', marginBottom: '3mm' }}>
            {ficheData.ordre?.numero_ordre}
          </div>

          {/* Image et Fontes */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2mm', marginBottom: '2mm' }}>
            {/* Image */}
            <div style={{ border: '1px solid black', padding: '2mm', height: '60mm', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {(() => {
                const rawUrl = ficheData.ordre?.image_url;
                const imgUrl = imagePrincipale || (rawUrl ? (rawUrl.startsWith('/') ? `${API_URL.replace('/api','')}${rawUrl.replace(/\\/g,'/')}` : rawUrl) : null);
                return imgUrl ? (
                  <img src={imgUrl} alt="Bijou"
                    style={{ maxWidth: '100%', maxHeight: '58mm', objectFit: 'contain' }}
                    onError={e => { e.target.style.display='none'; }}
                  />
                ) : (
                  <div style={{ textAlign: 'center', color: '#bbb', fontSize: '7pt' }}>
                    <div style={{ fontSize: '28pt', marginBottom: '2mm' }}>📷</div>
                    <div>Photo bijou</div>
                  </div>
                );
              })()}
            </div>

            {/* Fontes */}
            <div style={{ border: '1px solid black' }}>
              <div style={{ backgroundColor: '#f0f0f0', borderBottom: '1px solid black', padding: '1mm', textAlign: 'center', fontWeight: 'bold', fontSize: '8pt' }}>
                Fontes
              </div>
              <table style={{ width: '100%', fontSize: '7pt', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ borderRight: '1px solid black', padding: '1mm', textAlign: 'left' }}>Fontes</th>
                    <th style={{ padding: '1mm', textAlign: 'center' }}>Qté</th>
                  </tr>
                </thead>
                <tbody>
                  {fonte ? (
                    <tr>
                      <td style={{ borderRight: '1px solid black', padding: '1mm' }}>{fonte.numero_fonte}</td>
                      <td style={{ padding: '1mm', textAlign: 'center' }}>1</td>
                    </tr>
                  ) : (
                    <tr>
                      <td style={{ borderRight: '1px solid black', padding: '1mm' }}>—</td>
                      <td style={{ padding: '1mm', textAlign: 'center' }}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tableau Composants */}
          <div style={{ border: '1px solid black' }}>
            <table style={{ width: '100%', fontSize: '7pt', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f0f0f0' }}>
                  <th style={{ border: '1px solid black', padding: '1mm' }}>Composants</th>
                  <th style={{ border: '1px solid black', padding: '1mm' }}>Libellé</th>
                  <th style={{ border: '1px solid black', padding: '1mm' }}>Qté</th>
                  <th style={{ border: '1px solid black', padding: '1mm' }}>Poids</th>
                  <th style={{ border: '1px solid black', padding: '1mm' }}>Info</th>
                  <th style={{ border: '1px solid black', padding: '1mm' }}>Emplac.</th>
                </tr>
              </thead>
              <tbody>
                {/* Matières */}
                {(ficheData.matieres || ordre.matieres || []).map((matiere, idx) => (
                  <tr key={`mat-${idx}`}>
                    <td style={{ border: '1px solid black', padding: '1mm', fontWeight: 'bold' }}>
                      {matiere.reference || 'OR18K'}
                    </td>
                    <td style={{ border: '1px solid black', padding: '1mm' }}>
                      {matiere.designation || 'OR 18 K'}
                    </td>
                    <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                      {(parseFloat(matiere.quantite_grammes || matiere.quantite_prevue || matiere.quantite || 0) * quantitePieces).toFixed(2)}
                    </td>
                    <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                      {(parseFloat(matiere.quantite_grammes || matiere.quantite_prevue || matiere.quantite || 0) * quantitePieces).toFixed(2)}
                    </td>
                    <td style={{ border: '1px solid black', padding: '1mm' }}></td>
                    <td style={{ border: '1px solid black', padding: '1mm' }}></td>
                  </tr>
                ))}

                {/* Pierres */}
                {(ficheData.pierres || ordre.pierres || []).map((pierre, idx) => (
                  <tr key={`pie-${idx}`}>
                    <td style={{ border: '1px solid black', padding: '1mm', fontWeight: 'bold' }}>
                      {pierre.reference || 'DIA090'}
                    </td>
                    <td style={{ border: '1px solid black', padding: '1mm' }}>
                      {pierre.designation || 'DIAMANT ROND 9/11'}
                    </td>
                    <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                      {(parseFloat(pierre.quantite_prevue || pierre.quantite || 0) * quantitePieces).toFixed(2)}
                    </td>
                    <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>
                      {(parseFloat(pierre.poids_carats || 0) * quantitePieces).toFixed(2)}
                    </td>
                    <td style={{ border: '1px solid black', padding: '1mm' }}></td>
                    <td style={{ border: '1px solid black', padding: '1mm', textAlign: 'center' }}>DIA</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Numéro de série bijou — affiché uniquement quand terminé */}
      {ficheData.ordre?.id_produit && (
        <div className="no-print mt-4 bg-green-50 border-2 border-green-400 rounded-xl px-6 py-4 text-center shadow">
          <div className="text-xs text-green-600 font-semibold uppercase tracking-wide mb-1">Numéro de série bijou</div>
          <div className="text-4xl font-bold text-green-700 tracking-widest">{ficheData.ordre.id_produit}</div>
          <div className="text-xs text-green-500 mt-1">À retrouver dans le catalogue Bijoux</div>
        </div>
      )}

      {/* Modal Terminer depuis fiche */}
      {showTerminerFiche && etapeTerminer && (
        <div className="no-print fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-base font-bold text-gray-800 mb-1">✅ Terminer : {etapeTerminer.nom_etape}</h3>
            <p className="text-xs text-gray-500 mb-4">
              {etapeTerminer.ouvrier_nom ? `Ouvrier : ${etapeTerminer.ouvrier_nom}` : 'Le temps sera enregistré'}
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Temps réel (minutes)</label>
            <input type="number" min="1" value={tempsTerminer}
              onChange={e => setTempsTerminer(e.target.value)}
              className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-400 outline-none mb-3"
              placeholder={`Estimé: ${etapeTerminer.duree_estimee_heures ? Math.round(etapeTerminer.duree_estimee_heures * 60) : '?'} min`}
              autoFocus />

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Or utilisé (g)
              {ficheData?.resume?.or_total_donne > 0 && (
                <span className="ml-2 text-xs font-normal text-amber-600">
                  — Or donné OF : {parseFloat(ficheData.resume.or_total_donne).toFixed(3)} g
                </span>
              )}
            </label>
            <input type="number" step="0.001" min="0" value={poidsOrUtilise}
              onChange={e => setPoidsOrUtilise(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-300 outline-none mb-3"
              placeholder="Ex: 5.200 g (laisser vide si non applicable)" />

            <label className="block text-sm font-medium text-gray-700 mb-1">Poids bijou après étape (g)</label>
            <input type="number" step="0.001" min="0" value={poidsBijouEtape}
              onChange={e => setPoidsBijouEtape(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 outline-none mb-4"
              placeholder="Ex: 8.500 g (optionnel)" />

            <div className="flex gap-3">
              <button onClick={() => setShowTerminerFiche(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Annuler</button>
              <button onClick={validerTerminer}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700">Valider</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Réception externe depuis fiche */}
      {showReceptionFiche && etapeReception && (
        <div className="no-print fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-base font-bold text-orange-700 mb-1">📥 Réception : {etapeReception.nom_etape}</h3>
            <p className="text-xs text-gray-500 mb-4">Prestataire externe</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prix réel facturé (MAD)</label>
            <input type="number" step="0.01" min="0" value={formReception.prix_reel_externe}
              onChange={e => setFormReception(f => ({ ...f, prix_reel_externe: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3 outline-none focus:ring-2 focus:ring-orange-300"
              placeholder={`Estimé: ${etapeReception.prix_unitaire || 0} MAD`} />
            <label className="block text-sm font-medium text-gray-700 mb-1">Poids or retourné (g)</label>
            <input type="number" step="0.001" min="0" value={formReception.poids_or_recu_externe}
              onChange={e => setFormReception(f => ({ ...f, poids_or_recu_externe: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4 outline-none focus:ring-2 focus:ring-orange-300"
              placeholder="Ex: 9.800 g" />
            <div className="flex gap-3">
              <button onClick={() => setShowReceptionFiche(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Annuler</button>
              <button onClick={validerReception}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700">Valider</button>
            </div>
          </div>
        </div>
      )}

      {/* Styles d'impression */}
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          html, body {
            width: 297mm;
            height: 210mm;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }

          @page {
            size: A4 landscape;
            margin: 0;
          }

          .print-content {
            width: 297mm !important;
            height: 210mm !important;
            margin: 0 !important;
            padding: 6mm !important;
            overflow: hidden !important;
          }
        }
        
        @media screen {
          .print-content {
            box-shadow: 0 0 20px rgba(0,0,0,0.2);
          }
        }
      `}</style>
    </div>
  );
};

export default FicheFabricationV2;
